import { Editor } from '@tinymce/tinymce-react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

// Removes empty block elements (p, div) left over from ad slots, tracking placeholders, and
// web page layout — they create unwanted blank lines in the pasted content.
export const sanitizeEmptyBlocks = (body) => {
    body.querySelectorAll('p, div').forEach((node) => {
        if (!node.textContent.replace(/\s/g, '').length) node.remove()
    })
}

const RichTextEditor = (props) => {
    const { content, height, formik, fieldName, lang, onEditorInit, handleCarousel, setOverFlowWarning } = props

    const overflowWarningClosedRef = useRef(false)
    const [newContent, setNewContent] = useState(content)

    useEffect(() => {
        setNewContent(content ?? '')
    }, [content])

    const handleContentChange = useCallback((newContent) => {
        setNewContent(newContent)
    }, [])

    const handleDescriptionOverflow = (editor) => {
        // not need to track overflow if warning is closed
        if (overflowWarningClosedRef.current) return

        const editorBody = editor.getBody()
        const descriptionDiv = editorBody?.querySelector('.template-description, .template-6-description')

        if (descriptionDiv) {
            const isOverflowing = descriptionDiv.scrollHeight > descriptionDiv.clientHeight
            setOverFlowWarning(isOverflowing)
        }
    }

    return (
        <div>
            <Editor
                tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                value={newContent}
                onEditorChange={handleContentChange}
                id={`${fieldName}.${lang}`}
                init={{
                    license_key: 'gpl',
                    height: height || 500,
                    paste_as_text: false, // keep formatting (bold, underline, lists) when pasting from Word
                    invalid_elements: 'script,style,iframe,object',
                    branding: false,
                    promotion: false,
                    help_accessibility: false,
                    menubar: 'file edit view insert format tools table help print',
                    plugins: [
                        'advlist',
                        'anchor',
                        'autolink',
                        'charmap',
                        'code',
                        'fullscreen',
                        'help',
                        'image',
                        'insertdatetime',
                        'link',
                        'lists',
                        'media',
                        'preview',
                        'searchreplace',
                        'table',
                        'visualblocks',
                    ],
                    toolbar: `
                     undo redo | styles | bold italic underline strikethrough |
                     alignleft aligncenter alignright alignjustify | bullist numlist outdent indent |
                     link image
                    `,
                    content_css: '/styles/styles.css',
                    setup: (editor) => {
                        if (onEditorInit) {
                            onEditorInit(editor)
                        }

                        editor.on('SetContent', () => {
                            handleDescriptionOverflow(editor)
                        })

                        editor.on('PastePreProcess', (e) => {
                            const doc = new DOMParser().parseFromString(e.content, 'text/html')
                            sanitizeEmptyBlocks(doc.body)
                            e.content = doc.body.innerHTML
                        })

                        editor.on('click', (e) => {
                            // handle image carousel
                            const imageCarousel = e.target.closest('.img-container img')
                            if (imageCarousel) {
                                const photoIndex = e.target.getAttribute('data-photo-index')
                                const propertyId = e.target.getAttribute('data-property-id')
                                handleCarousel(photoIndex, propertyId)
                                return
                            }

                            // handle warning close button
                            const closeBtn = e.target.closest('.template-overflow-close')
                            if (closeBtn) {
                                setOverFlowWarning(false)
                                overflowWarningClosedRef.current = true
                            }
                        })

                        editor.on('input', () => {
                            const content = editor.getContent()

                            if (fieldName && lang) {
                                formik?.setFieldValue(`${fieldName}.${lang}`, content)
                            }
                        })

                        editor.on('change', () => {
                            const content = editor.getContent()

                            formik?.setFieldValue(`${fieldName}.${lang}`, content)
                        })

                        editor.on('remove', () => {
                            overflowWarningClosedRef.current = false
                        })
                    },
                }}
            />
        </div>
    )
}

export default memo(RichTextEditor)
